import axios from "axios";

// The public pages (SME / product / procurement listings) need no login, so
// their API endpoints can ask for a "public session" instead: proof that a real
// browser passed a Cloudflare Turnstile challenge. The challenge is normally
// invisible; its result is traded for a token that is then sent in
// X-Public-Token. See public-session-interceptor.js for when this runs.

export const PUBLIC_TOKEN_HEADER = "X-Public-Token";

const STORAGE_KEY = "smegp-public-session";
const WARM_UP_PAUSED_KEY = "smegp-public-session-paused-until";
// Stop using a token slightly before the API would reject it.
const EXPIRY_MARGIN_MS = 30 * 1000;
// Renew in the background once this share of a session's lifetime is left, so
// someone who keeps using the site never waits for a challenge again.
const RENEW_WHEN_REMAINING = 0.25;
// Lifetime assumed for a session stored before the lifetime was recorded.
const FALLBACK_LIFETIME_MS = 30 * 60 * 1000;
// After a background attempt fails (no guard configured, script blocked) leave
// the visitor alone for a while. A request the API rejects still tries at once.
const WARM_UP_PAUSE_MS = 60 * 60 * 1000;
const MAX_TIMER_MS = 24 * 60 * 60 * 1000;
const SCRIPT_TIMEOUT_MS = 10 * 1000;
// Generous: a visitor may have to notice and click an interactive challenge.
const CHALLENGE_TIMEOUT_MS = 2 * 60 * 1000;
const CHALLENGE_PROMPT = "กรุณายืนยันตัวตนเพื่อดูข้อมูล";

let session = null;
let pending = null;
// True while a request the API rejected is waiting for the running challenge.
let demand = false;
let pausedUntil = 0;
let renewalTimer = null;

function loadSession() {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    // Another tab may have renewed the session; use whichever lasts longer.
    if (stored && stored.token && (!session || stored.expiresAt > session.expiresAt)) session = stored;
  } catch (error) {
    // Storage can be unavailable (private mode); the in-memory copy still works.
  }
  return session;
}

function saveSession(token, expiresInSec) {
  const lifetime = expiresInSec * 1000;
  session = { token, expiresAt: Date.now() + lifetime, lifetime };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch (error) {
    // Kept in memory only.
  }
}

function clearSession() {
  session = null;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Nothing to clear.
  }
}

/** The current token, or null when there is none or it is about to expire. */
export function getPublicToken() {
  const current = loadSession();
  if (!current || !current.token) return null;
  if (Date.now() >= current.expiresAt - EXPIRY_MARGIN_MS) return null;
  return current.token;
}

// public/index.html loads the Turnstile script with `async defer`, so it may
// not have arrived yet — or never will, if a content blocker stops it.
function whenTurnstileReady() {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    (function poll() {
      if (window.turnstile) return resolve(window.turnstile);
      if (Date.now() - startedAt > SCRIPT_TIMEOUT_MS) {
        return reject(new Error("Turnstile script did not load"));
      }
      setTimeout(poll, 100);
    })();
  });
}

// Holds the widget. It takes no space and catches no clicks until Turnstile
// decides the visitor has to interact; only then does it dim the page and say
// why a checkbox appeared.
function createChallengeHost() {
  const root = document.createElement("div");
  root.style.cssText =
    "position:fixed;top:0;right:0;bottom:0;left:0;z-index:2147483000;display:flex;" +
    "flex-direction:column;align-items:center;justify-content:center;pointer-events:none;";

  const prompt = document.createElement("p");
  prompt.textContent = CHALLENGE_PROMPT;
  prompt.style.cssText = "display:none;margin:0 0 12px;color:#fff;font-size:1.1rem;text-align:center;";

  const slot = document.createElement("div");

  root.appendChild(prompt);
  root.appendChild(slot);
  document.body.appendChild(root);

  return {
    slot,
    show() {
      root.setAttribute("role", "dialog");
      root.setAttribute("aria-modal", "true");
      root.setAttribute("aria-label", CHALLENGE_PROMPT);
      root.style.background = "rgba(0,0,0,0.6)";
      root.style.pointerEvents = "auto";
      prompt.style.display = "block";
    },
    hide() {
      root.removeAttribute("role");
      root.removeAttribute("aria-modal");
      root.removeAttribute("aria-label");
      root.style.background = "";
      root.style.pointerEvents = "none";
      prompt.style.display = "none";
    },
    remove() {
      if (root.parentNode) root.parentNode.removeChild(root);
    },
  };
}

function solveChallenge() {
  return whenTurnstileReady().then(
    (turnstile) =>
      new Promise((resolve, reject) => {
        const host = createChallengeHost();
        let widgetId;

        const finish = (settle, value) => {
          clearTimeout(timer);
          try {
            turnstile.remove(widgetId);
          } catch (error) {
            // The widget never rendered.
          }
          host.remove();
          settle(value);
        };
        const timer = setTimeout(
          () => finish(reject, new Error("Turnstile challenge timed out")),
          CHALLENGE_TIMEOUT_MS
        );

        try {
          widgetId = turnstile.render(host.slot, {
            sitekey: process.env.VUE_APP_CLOUD_FLARE_TURNSTILE,
            action: "public_session",
            appearance: "interaction-only",
            // The widget is removed on the first error, so it must not try again
            // by itself; the next request that needs a session starts a new one.
            retry: "never",
            callback: (token) => finish(resolve, token),
            "error-callback": (code) => {
              finish(reject, new Error(`Turnstile challenge failed (${code})`));
              return true;
            },
            "before-interactive-callback": () => {
              // Only interrupt the visitor when something on the page is
              // actually waiting for this. A challenge started ahead of need
              // gives up instead; the next rejected request starts a new one.
              if (demand) host.show();
              else finish(reject, new Error("Turnstile wants interaction"));
            },
            "after-interactive-callback": () => host.hide(),
          });
        } catch (error) {
          finish(reject, error);
        }
      })
  );
}

function requestSession(turnstileToken) {
  return axios.post("/smegp/public/session", { token: turnstileToken }).then(({ data }) => {
    saveSession(data.token, data.expiresIn);
    return data.token;
  });
}

// Concurrent callers share one challenge, so a page firing several requests at
// once shows at most one.
function acquire() {
  if (!pending) {
    pending = solveChallenge()
      .then(requestSession)
      .then(
        (token) => {
          pending = null;
          demand = false;
          scheduleRenewal();
          return token;
        },
        (error) => {
          pending = null;
          demand = false;
          throw error;
        }
      );
  }
  return pending;
}

/**
 * For a request the API turned down: stop sending the current token and get a
 * fresh one. Joins a challenge that is already running.
 */
export function refreshPublicToken() {
  demand = true;
  if (!pending) clearSession();
  return acquire();
}

// How long before expiry a session should be replaced.
function renewalLead(current) {
  const lifetime = current.lifetime || FALLBACK_LIFETIME_MS;
  return Math.max(EXPIRY_MARGIN_MS, lifetime * RENEW_WHEN_REMAINING);
}

function needsRenewal() {
  const current = loadSession();
  if (!current || !current.token) return true;
  return current.expiresAt - Date.now() <= renewalLead(current);
}

function warmUpPaused() {
  let until = pausedUntil;
  try {
    until = Math.max(until, Number(localStorage.getItem(WARM_UP_PAUSED_KEY)) || 0);
  } catch (error) {
    // Only the in-memory value is known.
  }
  return Date.now() < until;
}

function pauseWarmUp() {
  pausedUntil = Date.now() + WARM_UP_PAUSE_MS;
  try {
    localStorage.setItem(WARM_UP_PAUSED_KEY, String(pausedUntil));
  } catch (error) {
    // Paused for this page only.
  }
  clearTimeout(renewalTimer);
  renewalTimer = setTimeout(warmUpPublicSession, WARM_UP_PAUSE_MS + 1000);
}

function scheduleRenewal() {
  clearTimeout(renewalTimer);
  const current = loadSession();
  if (!current || !current.token) return;
  const dueIn = current.expiresAt - Date.now() - renewalLead(current);
  renewalTimer = setTimeout(warmUpPublicSession, Math.min(Math.max(dueIn, 1000), MAX_TIMER_MS));
}

/**
 * Gets a session ahead of need: when there is none yet, and again shortly
 * before the current one runs out. The current token keeps working while its
 * replacement is fetched, so nothing on the page waits for this.
 */
export function warmUpPublicSession() {
  if (!needsRenewal()) return scheduleRenewal();
  if (pending || warmUpPaused()) return;
  // A challenge can stall in a tab nobody is looking at, and one visible tab
  // is enough to get a session for all of them. startPublicSessionWarmUp looks
  // again when this tab is shown.
  if (document.visibilityState === "hidden") return;
  acquire().catch(pauseWarmUp);
}

/** Call once when the app starts. Must never break start-up. */
export function startPublicSessionWarmUp() {
  try {
    warmUpPublicSession();
    // Timers are throttled or skipped while a tab is hidden or the device
    // sleeps, so look again whenever the visitor comes back.
    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") warmUpPublicSession();
    });
  } catch (error) {
    // The interceptor still obtains a session when the API asks for one.
  }
}
